// Pure helpers for the Intent page (unit tested).
import type { IntentExport, IntentExportRun, IntentSegment, IntentStage } from '@/apis/intentApi'

export const STAGE_LABEL: Record<IntentStage, string> = {
    browsing: 'Browsing',
    considering: 'Considering',
    intent: 'Ready to buy',
    purchased: 'Bought',
}

export const CONNECTOR_LABEL: Record<IntentExport['connector'], string> = {
    csv: 'CSV file',
    meta: 'Meta Custom Audience',
    google_ads: 'Google Ads Customer Match',
    klaviyo: 'Klaviyo list',
}

/**
 * Connector notes worth showing: for connectors this deployment allows or an export uses
 * (Google Customer Match eligibility, docs/intent.md §8).
 */
export function connectorNotesShown(
    notes: Record<string, string> | undefined,
    exports: Array<Pick<IntentExport, 'connector' | 'note'>>,
): Array<{ connector: IntentExport['connector']; note: string }> {
    const out = new Map<IntentExport['connector'], string>()
    for (const [c, note] of Object.entries(notes ?? {})) {
        if (c in CONNECTOR_LABEL) {
            out.set(c as IntentExport['connector'], note)
        }
    }
    for (const e of exports) {
        if (e.note && !out.has(e.connector)) {
            out.set(e.connector, e.note)
        }
    }
    return [...out].map(([connector, note]) => ({ connector, note }))
}

/** "Project" for the project's own traffic, else the sub-tenant id. */
export function memberLabel(subTenant: string): string {
    return subTenant === '' ? 'Project' : subTenant
}

/** "Sofas, Lighting" or "Any category". */
export function categoriesLabel(s: Pick<IntentSegment, 'categories'>): string {
    if (s.categories.length === 0) {
        return 'Any category'
    }
    return s.categories.map(c => c.path.split(' > ').pop() ?? c.path).join(', ')
}

export function stagesLabel(s: Pick<IntentSegment, 'definition'>): string {
    return s.definition.stages.map(st => STAGE_LABEL[st]).join(', ')
}

export type RunTone = 'default' | 'info' | 'success' | 'error'

export function runTone(run: IntentExportRun | null): RunTone {
    if (!run) {
        return 'default'
    }
    return run.status === 'completed' ? 'success' : run.status === 'failed' ? 'error' : 'info'
}

/** One line about the last run: what was sent and what the platform reported back. */
export function runSummary(run: IntentExportRun | null): string {
    if (!run) {
        return 'Not run yet'
    }
    if (run.status === 'failed') {
        return run.error ?? 'Failed'
    }
    if (run.status !== 'completed') {
        return run.status === 'queued'
            ? 'Waiting to start'
            : `Sending (${run.counts.sent ?? 0} so far)`
    }
    if (run.counts.note && !run.counts.sent) {
        return 'Nothing to send: no one in the segment has advertising consent and an email or phone'
    }
    const parts = [`${run.counts.sent ?? 0} sent`]
    if (run.counts.invalid) {
        parts.push(`${run.counts.invalid} rejected`)
    }
    const r = run.report ?? {}
    if (typeof r.match_rate_percentage === 'number') {
        parts.push(`${r.match_rate_percentage}% matched`)
    } else if (
        r.approximate_count_lower_bound !== undefined &&
        r.approximate_count_lower_bound !== null
    ) {
        parts.push(`audience about ${String(r.approximate_count_lower_bound)}`)
    }
    return parts.join(' · ')
}
